// src/pages/EventDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../lib/AuthContext';
import { getEvent, markInterested, removeInterest, getUserInterests, incrementEventView, registerForEvent } from '../lib/db';
import toast from 'react-hot-toast';

const REPEAT_LABEL = {
  weekly: '🔁 Every week', fortnightly: '🔁 Every two weeks',
  monthly: '🔁 Every month', custom: '🔁 Recurring'
};



const EventDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interested, setInterested] = useState(false);
  const [interestLoading, setInterestLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    const load = async () => {
      const ev = await getEvent(id);
      setEvent(ev);
      setLoading(false);
      if (ev) incrementEventView(id);
      // Check if user already interested
      if (user && ev) {
        const interests = await getUserInterests(user.uid);
        setInterested(interests.includes(id));
      }
    };
    load();
  }, [id, user]);

  const handleInterest = async () => {
    if (!user) { navigate('/login'); return; }
    setInterestLoading(true);
    try {
      if (interested) {
        await removeInterest(id, user.uid);
        setEvent(e => ({ ...e, interestCount: (e.interestCount || 1) - 1 }));
        setInterested(false);
      } else {
        await markInterested(id, user.uid);
        setEvent(e => ({ ...e, interestCount: (e.interestCount || 0) + 1 }));
        setInterested(true);
        toast.success('Saved! You\'re interested in this event');
      }
    } catch { toast.error('Something went wrong'); }
    setInterestLoading(false);
  };

  const handleRegister = async () => {
    if (!user) { navigate('/login'); return; }
    if (!regName.trim() || !regEmail.trim()) { toast.error('Please add your name and email'); return; }
    setRegistering(true);
    try {
      await registerForEvent(id, user.uid, regName.trim(), regEmail.trim(), event.nextDate || event.date);
      toast.success('You\'re registered!');
      setShowRegister(false);
      setRegName('');
      setRegEmail('');
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong — please try again');
    }
    setRegistering(false);
  };

  const handleShare = async () => {
    const url = window.location.href;
    const text = `${event?.title} — on Gather`;
    if (navigator.share) {
      try { await navigator.share({ title: event?.title, text, url }); }
      catch {}
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied!');
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date?.toDate ? date.toDate() : new Date(date);
    return format(d, 'EEEE d MMMM yyyy');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="empty-state" style={{ height: '100vh' }}>
        <div className="empty-state-icon">❌</div>
        <div className="empty-state-title">Event not found</div>
        <button className="btn btn-primary" style={{ marginTop: 16, maxWidth: 200 }} onClick={() => navigate('/')}>
          Back to home
        </button>
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', paddingBottom: 100 }}>

      {/* Hero */}
      <div style={s.hero}>
        {event.imageUrl
          ? <img src={event.imageUrl} alt={event.title} style={s.heroImg} />
          : <span style={{ fontSize: 70 }}>
              {{'music':'🎵','sport':'🏃','kids':'🧒','community':'🤝','markets':'🛒','arts':'🎭'}[event.category] || '📅'}
            </span>
        }
        <button style={s.backBtn} onClick={() => navigate(-1)}>←</button>
      </div>

      {/* Body */}
      <div style={s.body}>

        {/* Chips */}
        <div style={s.chips}>
          <span className="chip chip-date">{formatDate(event.nextDate || event.date)} · {event.time}</span>
          <span className="chip chip-sage" style={{ textTransform: 'capitalize' }}>{event.category}</span>
          {event.eventType === 'paid'
            ? <span className="chip chip-warm">💶 {event.eventCharge}</span>
            : <span className="chip chip-sage">Free entry</span>
          }
          {event.repeat && event.repeat !== 'once' && (
            <span className="chip chip-muted">{REPEAT_LABEL[event.repeat]}</span>
          )}
        </div>

        {/* Title */}
        <h1 style={s.title}>{event.title}</h1>

        {/* Organiser */}
        <div style={{ ...s.orgRow, cursor: 'pointer' }} onClick={() => navigate(`/organisers/${event.organiserId}`)}>
          <div style={s.orgAv}>🎸</div>
          <div style={{ flex: 1 }}>
            <div style={s.orgName}>{event.organiserName}</div>
            <div style={s.orgRole}>✓ Verified organiser · View profile & follow</div>
          </div>
          <span style={{ color: 'var(--border)', fontSize: 20, flexShrink: 0 }}>›</span>
        </div>

        {/* Interest count */}
        {event.interestCount > 0 && (
          <div style={s.interestRow}>
            <div style={s.intAvatars}>
              {['S','M','A'].map((l, i) => (
                <div key={i} style={{ ...s.intAv, background: i === 0 ? 'var(--sage)' : 'var(--sage-light)' }}>{l}</div>
              ))}
            </div>
            <div style={s.intText}>
              <strong>{event.interestCount} {event.interestCount === 1 ? 'person is' : 'people are'}</strong> interested in this
            </div>
          </div>
        )}

        {/* Description */}
        <div style={s.secLabel}>About this event</div>
        <div style={s.desc}>{event.description}</div>

        {/* Location */}
        <div style={s.secLabel}>Where</div>
        <div style={s.locBox}>
          <span style={{ fontSize: 22 }}>📍</span>
          <div>
            <div style={s.locName}>{event.venue}</div>
            {event.townName && <div style={s.locAddr}>{event.townName}, Co. Galway</div>}
          </div>
        </div>

        {/* Register form (free events with headcount) */}
        {showRegister && (
          <div style={s.registerForm}>
            <div style={s.secLabel}>Register your place</div>
            <input className="form-input" style={{ marginBottom: 10 }} placeholder="Your name" value={regName} onChange={e => setRegName(e.target.value)} />
            <input className="form-input" placeholder="Your email" value={regEmail} onChange={e => setRegEmail(e.target.value)} />
            <button style={{ ...s.actionBtn, background: 'var(--sage-deep)', color: 'white', marginTop: 10, opacity: registering ? 0.7 : 1 }}
              onClick={handleRegister} disabled={registering}>
              {registering ? 'Registering...' : 'Confirm my place'}
            </button>
          </div>
        )}
      </div>

      

      

      {/* Action bar */}
      <div style={s.actionBar}>
        <button style={{ ...s.actionBtn, background: 'var(--cream)', flex: 0.35 }} onClick={() => navigate(-1)}>←</button>

        {event.eventType === 'paid' ? (
          <>
            <button
              style={{ ...s.actionBtn, flex: 0.35, background: 'var(--sage-pale)', color: 'var(--sage-dark)', fontSize: 20, opacity: interestLoading ? 0.7 : 1 }}
              onClick={handleInterest}
              aria-label={interested ? 'Remove interest' : 'Mark interested'}
            >
              {interested ? '❤️' : '🤍'}
            </button>
            {event.bookingUrl ? (
              <button style={{ ...s.actionBtn, background: 'var(--warm)', color: 'white' }}
                onClick={() => window.open(event.bookingUrl, '_blank', 'noopener,noreferrer')}>
                💶 {event.eventCharge} · Booking info
              </button>
            ) : (
              <div style={{ ...s.actionBtn, background: 'var(--cream)', color: '#2C4A2E', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                💶 {event.eventCharge}
              </div>
            )}
          </>
        ) : event.eventType === 'register' ? (
          <button style={{ ...s.actionBtn, background: 'var(--sage-deep)', color: 'white' }}
            onClick={() => setShowRegister(true)}>
            📋 Register free place
          </button>
        ) : (
          <button
            style={{ ...s.actionBtn, background: 'var(--sage-pale)', color: 'var(--sage-dark)', opacity: interestLoading ? 0.7 : 1 }}
            onClick={handleInterest}
          >
            {interested ? '❤️ Interested!' : '🤍 Interested'}
          </button>
        )}

<button style={{ ...s.actionBtn, background: 'var(--sage-deep)', color: 'white', flex: 0.5 }} onClick={handleShare}>
          Share
        </button>
      </div>

      {/* Organiser contact — only shown when organiser has provided the details */}
      {(event.organiserPhone || event.organiserEmail || event.bookingUrl) && (
        <div style={s.contactBar}>
          {event.organiserPhone && (
            <a href={`tel:${event.organiserPhone}`} style={s.contactBtn}>
              📞 Call organiser
            </a>
          )}
          {event.organiserPhone && (
            <a href={`sms:${event.organiserPhone}`} style={s.contactBtnSec}>
              💬 Text organiser
            </a>
          )}
          {event.organiserEmail && (
            <a href={`mailto:${event.organiserEmail}`} style={s.contactBtnSec}>
              ✉️ Email organiser
            </a>
          )}
          {event.bookingUrl && (
            <a href={event.bookingUrl} target="_blank" rel="noopener noreferrer" style={s.contactBtnSec}>
              🔗 Booking link
            </a>
          )}
        </div>
      )}
    </div>
  );
};

const s = {
  hero: {
    width: '100%', height: 220,
    background: 'linear-gradient(140deg, var(--sage-light), var(--sage))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    position: 'relative',
  },
  heroImg: { width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', inset: 0, background: '#F2F7F2' },
  backBtn: {
    position: 'absolute', top: 52, left: 18,
    width: 36, height: 36, background: 'rgba(255,255,255,0.7)',
    border: 'none', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 16, cursor: 'pointer', color: 'var(--sage-dark)',
    backdropFilter: 'blur(6px)',
  },
  body: { padding: '20px 20px 20px' },
  chips: { display: 'flex', gap: 7, marginBottom: 14, flexWrap: 'wrap' },
  title: { fontSize: 26, fontWeight: 800, color: 'var(--ink)', lineHeight: 1.2, marginBottom: 14, letterSpacing: '-0.3px' },
  orgRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    background: 'var(--white)', borderRadius: 14, padding: 13, marginBottom: 12,
    boxShadow: 'var(--shadow-sm)',
  },
  orgAv: { width: 40, height: 40, background: 'var(--sage-pale)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19 },
  orgName: { fontSize: 14, fontWeight: 700, color: 'var(--ink)' },
  orgRole: { fontSize: 12, color: 'var(--sage-deep)', fontWeight: 600 },
  interestRow: {
    background: 'var(--white)', borderRadius: 14, padding: 13,
    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
    boxShadow: 'var(--shadow-sm)',
  },
  intAvatars: { display: 'flex' },
  intAv: {
    width: 26, height: 26, borderRadius: '50%',
    border: '2px solid var(--white)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 700, marginRight: -7, color: 'var(--sage-dark)',
  },
  intText: { fontSize: 13, color: 'var(--ink)', fontWeight: 500, marginLeft: 14 },
  secLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', margin: '16px 0 8px' },
  desc: { fontSize: 15, lineHeight: 1.7, color: '#505A50', fontWeight: 500 },
  locBox: {
    background: 'var(--white)', borderRadius: 14, padding: 13,
    display: 'flex', alignItems: 'center', gap: 12,
    boxShadow: 'var(--shadow-sm)',
  },
  locName: { fontSize: 14, fontWeight: 700, color: 'var(--ink)' },
  locAddr: { fontSize: 12, color: 'var(--muted)', fontWeight: 500 },
  registerForm: { marginTop: 16 },
  actionBar: {
    position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
    width: '100%', maxWidth: 430,
    padding: '13px 18px 26px',
    background: 'var(--white)', borderTop: '1.5px solid var(--border)',
    display: 'flex', gap: 10, zIndex: 100,
  },
  contactBar: {
    position: 'fixed', bottom: 88, left: '50%', transform: 'translateX(-50%)',
    width: '100%', maxWidth: 430,
    padding: '10px 18px',
    display: 'flex', gap: 8, zIndex: 99,
  },
  contactBtn: {
    flex: 1, padding: '11px 10px', borderRadius: 12,
    background: 'var(--sage-deep)', color: 'white',
    fontSize: 13, fontWeight: 700, textAlign: 'center',
    textDecoration: 'none', display: 'inline-block',
  },
  contactBtnSec: {
    flex: 1, padding: '11px 10px', borderRadius: 12,
    background: 'var(--sage-pale)', color: 'var(--sage-dark)',
    fontSize: 13, fontWeight: 700, textAlign: 'center',
    textDecoration: 'none', display: 'inline-block',
  },
  actionBtn: {
    flex: 1, padding: 15, borderRadius: 14, border: 'none',
    fontSize: 14, fontWeight: 700, fontFamily: 'Nunito, sans-serif',
    cursor: 'pointer', transition: 'transform 0.15s',
  },
};

export default EventDetailPage;
